<script setup lang="ts">
/**
 * 商品画像パネル(一覧・拡大・OCR表示・画像編集画面の入口・一括処理)。
 *
 * 【画像編集の作り直し】
 * 以前ここにあった「Amazonから画像更新」「画像ファイルを追加」「削除(×)」「前へ/後へ」は、
 * 押した瞬間にDBとS3を書き換えていた(Amazon更新は今の画像を全部置き換えてしまい、見比べられなかった)。
 * これらは画像編集画面(imageEditor/ImageEditorModal.vue)にまとめ、登録ボタンで1回だけ反映する形にした。
 * このパネルは表示と、画像編集画面・一括処理(兄弟ASINへ一括追加 / 類似画像の一括置換・削除)の入口だけを持つ。
 *
 * VB.NET版 ImageDirector.exe のWeb化(フェーズ1〜6)。
 *
 * 元々は審査画面(OfferDetailPanel.vue)専用のインライン実装だったが、フェーズ5で
 * セルモン商品編集(ProductEditView.vue)からも同じ機能を使えるようにするため、
 * asin/regionCode を props として受け取る独立コンポーネントに切り出した。
 * (審査画面側は selection ストアの selectedAsin をそのまま props として渡す)
 *
 * 【画像OCR NGワードチェック】
 * getProductImages が返す画像ごとの ocr(照合済みのNGワードと枠の座標)を表示する。
 *   - サムネイル: 画像番号の右隣に「OCR未」「NGなし」、またはNGワードのレベル別件数を色分けで出す
 *     (error / check_failed の画像は何も出さない)
 *   - クリック拡大(ライトボックス): 画像に枠を重ね、画像の下にNGワード一覧を出す。
 *     一覧の行にマウスを乗せると、その語の枠を強調する。「枠を表示」で枠の表示/非表示を切り替える
 *     (設定は localStorage に保存し、画像送り・次回表示でも維持する)
 *   - ホバー拡大: OCRの表示はしない(元画像だけを見る用途のため)
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useProductImages } from '@/api/queries'
import { useToastStore } from '@/stores/toast'
import ImageEditorModal from './imageEditor/ImageEditorModal.vue'
import { keyFromImageUrl } from '@/utils/imageEditor'
import SimilarImagesModal from './SimilarImagesModal.vue'
import BulkAddImagesModal from './BulkAddImagesModal.vue'
import OcrImage from './OcrImage.vue'
import OcrNgList from './OcrNgList.vue'
import { ocrBadgeState, ocrHits } from '@/utils/ocrNg'

const props = withDefaults(
  defineProps<{
    asin: string | null
    regionCode?: string
    parentAsin?: string | null
  }>(),
  { regionCode: 'jp' },
)

const toast = useToastStore()

/** 画像編集画面を開いているか */
const editorOpen = ref(false)

const asinRef = computed(() => props.asin)
const { data: imagesData, isLoading: isImagesLoading } = useProductImages(asinRef)

// ---- 画像一括処理(類似画像の一括置換/削除、兄弟ASINへの一括追加) ----
// VB版 SalmonJudgment/ConfirmImageListWindow.xaml.vb 相当。
const bulkModalMode = ref<'replace' | 'delete' | 'add' | null>(null)
const bulkModalImageNo = ref<number | null>(null)
const menuOpenForImageNo = ref<number | null>(null)
// 「一括」メニューはタイルの overflow:hidden の外側(Teleport先)に描画するため、
// 表示位置をボタン要素の座標から計算して保持する。
const menuPos = ref({ x: 0, y: 0, width: 0 })

function toggleTileMenu(imageNo: number, e: MouseEvent) {
  if (menuOpenForImageNo.value === imageNo) {
    menuOpenForImageNo.value = null
    return
  }
  const btn = (e.currentTarget as HTMLElement).getBoundingClientRect()
  menuPos.value = { x: btn.left, y: btn.bottom + 2, width: btn.width }
  menuOpenForImageNo.value = imageNo
}

// 「一括」メニューはクリックしなくてもホバーで開くようにする。
// メニュー自体はTeleportでbody直下に描画されるため、ボタンとメニューの
// どちらの上にカーソルがあっても開いたままになるよう、少し遅延させてから閉じる。
let closeTileMenuTimer: ReturnType<typeof setTimeout> | null = null

function openTileMenuHover(imageNo: number, e: MouseEvent) {
  cancelCloseTileMenu()
  // wrap要素(ボタンを内包する.img-bulk-menu-wrap)の座標を基準に表示位置を決める
  const wrap = (e.currentTarget as HTMLElement).querySelector('.img-bulk-btn') ?? (e.currentTarget as HTMLElement)
  const rect = wrap.getBoundingClientRect()
  menuPos.value = { x: rect.left, y: rect.bottom + 2, width: rect.width }
  menuOpenForImageNo.value = imageNo
}
function cancelCloseTileMenu() {
  if (closeTileMenuTimer !== null) {
    clearTimeout(closeTileMenuTimer)
    closeTileMenuTimer = null
  }
}
function scheduleCloseTileMenu() {
  cancelCloseTileMenu()
  closeTileMenuTimer = setTimeout(() => {
    menuOpenForImageNo.value = null
    closeTileMenuTimer = null
  }, 200)
}
function openBulkModal(mode: 'replace' | 'delete' | 'add', imageNo: number) {
  bulkModalMode.value = mode
  bulkModalImageNo.value = imageNo
  menuOpenForImageNo.value = null
}
/**
 * 「この画像を兄弟ASINに一括追加」: 選んだ1枚を、兄弟ASIN(この商品を除く)の末尾に追加する。
 * 処理は「兄弟ASINへ一括追加」と同じ(bulkAddSiblingImages)。ファイルを選ぶ代わりにこの画像をコピー元にする。
 */
const siblingAddPreset = ref<{ sourceKey: string; previewUrl: string }[] | null>(null)
function openSiblingAdd(imageNo: number) {
  menuOpenForImageNo.value = null
  const img = imagesData.value?.images.find((i) => i.ImageNo === imageNo && i.ImageTypeId === 5)
    ?? imagesData.value?.images.find((i) => i.ImageNo === imageNo)
  const key = img ? keyFromImageUrl(img.ImageUrl) : null
  if (!img || !key) {
    toast.show('この画像は兄弟ASINへ追加できません。', 'ng')
    return
  }
  siblingAddPreset.value = [{ sourceKey: key, previewUrl: img.ImageUrl }]
}

function closeBulkModal() {
  bulkModalMode.value = null
  bulkModalImageNo.value = null
}

// ---- 画像拡大表示(ライトボックス) ----
const lightboxIndex = ref<number | null>(null)
const lightboxImages = computed(() => imagesData.value?.images ?? [])

const lightboxUrl = computed(() =>
  lightboxIndex.value !== null ? lightboxImages.value[lightboxIndex.value]?.ImageUrl ?? null : null,
)
const lightboxImageNo = computed(() =>
  lightboxIndex.value !== null ? lightboxImages.value[lightboxIndex.value]?.ImageNo ?? null : null,
)
// サムネイルのOCRバッジ(画像の並びと同じ順)
const ocrBadges = computed(() => (imagesData.value?.images ?? []).map((img) => ocrBadgeState(img.ocr)))
function hitCounts(idx: number) {
  const st = ocrBadges.value[idx]
  return st?.kind === 'hits' ? st.counts : []
}

const lightboxOcr = computed(() =>
  lightboxIndex.value !== null ? lightboxImages.value[lightboxIndex.value]?.ocr : undefined,
)
const lightboxHits = computed(() => ocrHits(lightboxOcr.value))
/** NGワード一覧でマウスが乗っている行(=枠を強調する語)の添字 */
const activeHitIndex = ref<number | null>(null)
// 画像を切り替えたら強調を解除する(前の画像の添字が別の語を指してしまうため)
watch(lightboxIndex, () => (activeHitIndex.value = null))

/** ライトボックスで画像上の枠を表示するか。画像送りのたびに戻らないよう localStorage に保存する */
const OCR_BOX_STORAGE_KEY = 'salmon.judge.ocrShowBoxes'
function loadShowOcrBoxes(): boolean {
  try {
    return localStorage.getItem(OCR_BOX_STORAGE_KEY) !== '0'
  } catch {
    return true
  }
}
const showOcrBoxes = ref(loadShowOcrBoxes())
watch(showOcrBoxes, (v) => {
  try {
    localStorage.setItem(OCR_BOX_STORAGE_KEY, v ? '1' : '0')
  } catch {
    /* 保存できなくても表示の切り替え自体は動く */
  }
})

/**
 * 画像の周りの余白(画像より枠が大きいとき)をクリックしたら閉じる。
 * 枠(lightbox-stage)は画像サイズに関わらず固定の大きさにしたため、
 * 見た目は背景と同じ余白部分でも閉じられるようにしておく。
 */
function onStageClick(e: MouseEvent) {
  if (e.target === e.currentTarget) closeLightbox()
}

function openLightbox(index: number) {
  lightboxIndex.value = index
}
function closeLightbox() {
  lightboxIndex.value = null
}

// ---- マウスホバー/クリックでの拡大表示 ----
// 実際の描画は OfferDetailPanel の右ゾーン(商品属性の右隣)で行う。
// ここでは「どの画像を出すか」だけをストアに伝える。
// カーソル追従のオーバーレイにしないのは、特徴・商品説明の上に重なると
// 画像と本文の対比ができなくなるため。
function onTileMouseEnter(index: number) {
  hoverIndex.value = index
}

// マウスオーバーで画面全面に大きく出す。
// 拡大専用エリアを廃止したため、詳細確認はこのオーバーレイで行う。
// (OCR結果の自動再取得で一覧が更新されても追従できるよう、URLではなく添字で持つ)
const hoverIndex = ref<number | null>(null)
const hoverImage = computed(() =>
  hoverIndex.value !== null ? imagesData.value?.images[hoverIndex.value] ?? null : null,
)
function onTileMouseLeave() {
  hoverIndex.value = null
}
function showPrevImage() {
  if (lightboxIndex.value === null) return
  lightboxIndex.value = (lightboxIndex.value - 1 + lightboxImages.value.length) % lightboxImages.value.length
}
function showNextImage() {
  if (lightboxIndex.value === null) return
  lightboxIndex.value = (lightboxIndex.value + 1) % lightboxImages.value.length
}
function onLightboxKeydown(e: KeyboardEvent) {
  if (lightboxIndex.value === null) return
  if (e.key === 'ArrowLeft') showPrevImage()
  else if (e.key === 'ArrowRight') showNextImage()
  else if (e.key === 'Escape') closeLightbox()
}
onMounted(() => window.addEventListener('keydown', onLightboxKeydown))
onUnmounted(() => window.removeEventListener('keydown', onLightboxKeydown))

// ---- 画像編集(アップロード・削除) ----
// 表示専用モードは廃止し、常に編集モードのUIを表示する(VB.NET版のImageDirector.exeも
// 常に編集操作が可能な単一モードだったため、それに合わせる)。

// 代表画像(ImageNo=0)の有無チェック。VB版 SearchImage の bZero 判定相当。
const hasNoRepresentativeImage = computed(() => {
  const images = imagesData.value?.images
  if (!images || images.length === 0) return false
  return !images.some((img) => img.ImageNo === 0)
})

// 画像URLコピー。VB版 ButtonImageCopy_Click(Clipboard.SetText)相当。
async function copyImageUrl(url: string) {
  try {
    await navigator.clipboard.writeText(url)
    toast.show('画像のURLをコピーしました。')
  } catch {
    window.prompt('クリップボードにコピーできませんでした。以下のURLを手動でコピーしてください:', url)
  }
}
</script>

<template>
  <div class="card">
    <!-- タイトルは不要のため出さない -->
    <div class="cat-edit-bar">
      <button
        class="btn btn-primary btn-sm"
        title="現在の画像・Amazonの画像・AI修正・ローカルファイルを見比べながら編集し、登録ボタンでまとめて反映します"
        :disabled="!asin"
        @click="editorOpen = true"
      >
        <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
        画像を編集
      </button>
      <button
        class="btn btn-ghost btn-sm"
        title="この商品と同じ親を持つ兄弟ASIN(バリエーション違い等)全体に画像をまとめて追加します"
        @click="bulkModalMode = 'add'"
      >
        <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        兄弟ASINへ一括追加
      </button>
    </div>

    <div v-if="isImagesLoading" class="img-strip">
      <div class="img-tile-loading" v-for="n in 3" :key="n">
        <span class="spinner"></span>
      </div>
    </div>
    <div v-else-if="!imagesData || imagesData.images.length === 0" class="empty-state" style="padding:16px 0;">
      画像がありません
    </div>
    <template v-else>
      <div v-if="hasNoRepresentativeImage" class="img-warning">
        代表画像がありません
      </div>
      <div class="img-strip">
        <div class="img-tile" v-for="(img, idx) in imagesData.images" :key="`${img.ImageTypeId}-${img.ImageNo}`">
          <!-- 固定表示は廃止。クリックでそのまま全画面を開く
               (以前はクリック=固定/ダブルクリック=全画面だったが、
               固定機能自体が不要になったため単純化した)。 -->
          <div
            class="img-frame"
            title="クリックで全画面表示"
            @click="openLightbox(idx)"
            @mouseenter="onTileMouseEnter(idx)"
            @mouseleave="onTileMouseLeave"
          >
            <img :src="img.ImageUrl" :alt="`${img.ASIN}-${img.ImageNo}`" loading="lazy" />
          </div>
          <!-- 番号・コピー・一括を1行にまとめる。
               並び順は 番号(左) → 空き → コピー → 一括(右端)。
               コピーが一括の左隣に来るよう、同じflex行の中に置く。 -->
          <div class="img-footer">
            <div class="img-footer-main">
              <span class="img-no-badge">{{ img.ImageNo }}</span>
              <!-- 画像OCR NGワードチェックの結果(番号の右隣)。error / check_failed は出さない -->
              <template v-if="ocrBadges[idx]">
                <span v-if="ocrBadges[idx]!.kind === 'pending'" class="ocr-badge ocr-badge-pending" title="まだOCRされていません">OCR未</span>
                <span v-else-if="ocrBadges[idx]!.kind === 'clean'" class="ocr-badge ocr-badge-clean" title="画像内にNGワードはありません">NGなし</span>
                <template v-else>
                  <span
                    v-for="c in hitCounts(idx)"
                    :key="c.level"
                    class="ocr-badge ocr-badge-lv"
                    :class="`lv${c.level}`"
                    :title="`画像内のNGワード Lv.${c.level === 5 ? '5以下' : c.level}: ${c.count}件`"
                  >NG<span class="ocr-badge-n">{{ c.count }}</span></span>
                </template>
              </template>
            </div>
            <div class="img-footer-actions">
              <button
                class="img-copy-btn"
                title="この画像のURLをコピー"
                @click="copyImageUrl(img.ImageUrl)"
              >
                <svg class="icon" viewBox="0 0 24 24" style="width:13px;height:13px;"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
              <div
                class="img-bulk-menu-wrap"
                @mouseenter="openTileMenuHover(img.ImageNo, $event)"
                @mouseleave="scheduleCloseTileMenu"
              >
                <button
                  class="img-bulk-btn"
                  title="この画像と同一の画像に対する一括処理"
                  @click.stop="toggleTileMenu(img.ImageNo, $event)"
                >
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                  一括
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>

  <!-- 画像拡大ライトボックス。
       【操作位置の固定】画像の枠(.lightbox-stage)は画像サイズに関係なく常に同じ大きさ・位置にし、
       画像はその中央に置く。前へ/次へ・×・番号はこの枠を基準に配置するため、
         - 画像の縦横比やサイズが違っても、ボタンの位置が動かない
         - 画像の下にNGワード一覧が出る/出ないでも、画像とボタンの位置が動かない
           (全体を上端基準で配置し、一覧は画像の枠の下に追加で表示するだけにしている)
       前へ/次へは、枠の左右に枠と同じ高さの縦長のクリック領域として置き、
       画像送りで多少マウス位置がずれても押せるようにしている。 -->
  <div v-if="lightboxUrl" class="lightbox-overlay" @click="closeLightbox">
    <div class="lightbox-body" @click.stop>
      <div class="lightbox-stage" @click.stop="onStageClick">
        <button class="lightbox-close" title="閉じる (Esc)" @click.stop="closeLightbox">
          <svg class="icon" viewBox="0 0 24 24" style="color:#fff;width:20px;height:20px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        <button
          v-if="lightboxImages.length > 1"
          class="lightbox-nav lightbox-nav-prev"
          title="前の画像 (←)"
          @click.stop="showPrevImage"
        >
          <span class="lightbox-nav-circle">
            <svg class="icon" viewBox="0 0 24 24" style="width:22px;height:22px;"><path d="M15 18l-6-6 6-6" /></svg>
          </span>
        </button>

        <span v-if="lightboxImageNo !== null" class="img-no-badge img-no-badge-lg lightbox-no-badge">{{ lightboxImageNo }}</span>
        <OcrImage
          :src="lightboxUrl"
          :ocr="lightboxOcr"
          :active-index="activeHitIndex"
          :show-all="showOcrBoxes"
          img-class="lightbox-image"
        />

        <button
          v-if="lightboxImages.length > 1"
          class="lightbox-nav lightbox-nav-next"
          title="次の画像 (→)"
          @click.stop="showNextImage"
        >
          <span class="lightbox-nav-circle">
            <svg class="icon" viewBox="0 0 24 24" style="width:22px;height:22px;"><path d="M9 6l6 6-6 6" /></svg>
          </span>
        </button>

        <div v-if="lightboxImages.length > 1" class="lightbox-counter">
          {{ (lightboxIndex ?? 0) + 1 }} / {{ lightboxImages.length }}
        </div>
      </div>

      <!-- 画像内のNGワード一覧(OCR済みでヒットがあるときだけ) -->
      <OcrNgList
        v-if="lightboxHits.length > 0"
        v-model:show-boxes="showOcrBoxes"
        :hits="lightboxHits"
        :active-index="activeHitIndex"
        @hover="activeHitIndex = $event"
      />
    </div>
  </div>

  <ImageEditorModal
    v-if="editorOpen && asin"
    :asin="asin"
    :region-code="regionCode"
    :parent-asin="props.parentAsin"
    @close="editorOpen = false"
  />

  <SimilarImagesModal
    v-if="(bulkModalMode === 'replace' || bulkModalMode === 'delete') && asin && bulkModalImageNo !== null"
    :mode="bulkModalMode"
    :asin="asin"
    :image-no="bulkModalImageNo"
    :region-code="regionCode"
    @close="closeBulkModal"
  />
  <BulkAddImagesModal
    v-if="siblingAddPreset && asin"
    :asin="asin"
    :region-code="regionCode"
    :parent-asin="props.parentAsin"
    :preset-sources="siblingAddPreset"
    exclude-basis
    @close="siblingAddPreset = null"
  />
  <BulkAddImagesModal
    v-if="bulkModalMode === 'add' && asin"
    :asin="asin"
    :region-code="regionCode"
    :parent-asin="props.parentAsin"
    @close="closeBulkModal"
  />

  <!-- 「一括」メニュー本体。タイルのoverflow:hiddenで見切れないようbody直下に描画する -->
  <!-- ホバー中の全面拡大。pointer-events:none なので操作の邪魔をしない -->
  <Teleport to="body">
    <div v-if="hoverImage" class="img-hover-full">
      <img :src="hoverImage.ImageUrl" alt="拡大表示" />
    </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="menuOpenForImageNo !== null"
      class="img-bulk-menu"
      :style="{ left: menuPos.x + 'px', top: menuPos.y + 'px', minWidth: Math.max(menuPos.width, 200) + 'px' }"
      @click.stop
      @mouseenter="cancelCloseTileMenu"
      @mouseleave="scheduleCloseTileMenu"
    >
      <div class="img-bulk-menu-label">この画像と同一の画像に対して</div>
      <button @click="openBulkModal('replace', menuOpenForImageNo!)">
        <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        まとめて置換...
      </button>
      <button class="img-bulk-menu-danger" @click="openBulkModal('delete', menuOpenForImageNo!)">
        <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/></svg>
        まとめて削除...
      </button>
      <div class="img-bulk-menu-label img-bulk-menu-label-sep">この画像を</div>
      <button @click="openSiblingAdd(menuOpenForImageNo!)">
        <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        兄弟ASINに一括追加...
      </button>
    </div>
  </Teleport>
  <!-- メニュー表示中は画面のどこかをクリックしたら閉じる -->
  <Teleport to="body">
    <div v-if="menuOpenForImageNo !== null" class="img-bulk-menu-scrim" @click="menuOpenForImageNo = null"></div>
  </Teleport>
</template>
